import React from 'react';
import { styled, keyframes } from '@compiled/react';
import { token } from '@atlaskit/tokens';

const IssueRowSkeleton = () => (
	<IssueRowSkeletonWrapper>
		<IssueTypeSkeleton />
		<IssueFieldSkeleton />
		<IssueSummarySkeleton />
		<IssueFieldSkeleton />
		<IssueAssigneeSkeleton />
		<IssueDevDataSkeleton />
		<IssueDevDataSkeleton />
		<IssueDevDataSkeleton />
	</IssueRowSkeletonWrapper>
);

type Props = {
	rowCount: number;
	marginTop?: number;
};

export const IssueListSkeleton = ({ rowCount, marginTop }: Props): React.JSX.Element => (
	<Wrapper marginTop={marginTop}>
		{/* Somehow rowCount is negative, and couldn't get to the bottom of the reason how possibly this happens. For loading, displaying 10 rows, that doesn't match with the actual result, is not end of the world, so we will fallback to 10 if a negative number is given. */}
		{/* The best guess why this happens is BE has SearchResults#isTotalCountAnEstimate value. When that is true, totalCount is not reliable. */}
		{[...Array(rowCount < 0 ? 10 : rowCount).keys()].map((index) => (
			<IssueRowSkeleton key={index} />
		))}
	</Wrapper>
);

interface WrapperProps {
	marginTop?: number;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<WrapperProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ marginTop }) => (marginTop ? `${marginTop}px` : 0),
});

const skeletonKeyFrames = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '2000px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AnimationBaseDiv = styled.div({
	animationDelay: '0s',
	animationDuration: '2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
	backgroundImage: `linear-gradient( to right, ${token('color.skeleton')} 10%, ${token('color.skeleton.subtle')} 30%, ${token('color.skeleton')} 50% )`,
	backgroundRepeat: 'no-repeat',

	backgroundColor: token('color.skeleton'),
	animationName: skeletonKeyFrames,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueRowSkeletonWrapper = styled.div({
	height: '48px',
	paddingLeft: token('space.200'),
	paddingRight: token('space.600'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueTypeSkeleton = styled(AnimationBaseDiv)({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	height: '16px',
	width: '16px',
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueFieldSkeleton = styled(AnimationBaseDiv)({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	height: '16px',
	width: '64px',
	marginRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueDevDataSkeleton = styled(AnimationBaseDiv)({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	height: '16px',
	width: '64px',
	marginRight: token('space.400'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueSummarySkeleton = styled(AnimationBaseDiv)({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	height: '16px',

	marginRight: '96px',
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueAssigneeSkeleton = styled(AnimationBaseDiv)({
	borderRadius: token('radius.full'),
	height: '24px',
	width: '24px',
	marginRight: token('space.800'),
});
